import * as React from "react";
import { Box, useI18nContext, ToolLandingPage, SplitViewCard } from "@procore/core-react";
import type { CellValueChangeParams, TableApi } from "@procore/data-table";
import { ProjectStore } from "@/stores/project-store.core";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { ProjectListDataTable } from "./project-list-data-table";
import { useGetGroupOptionsQuery } from "../common/queries/queries";
import { filterRendererMap } from "./project-list-filter-renderer-map";
import {
   filterGroupDetails,
   convertProjectDataTableFilters,
   convertDataTableSort,
   filterGroupIds,
   columnHeadersMap,
   defaultProjectListTableConfig,
   filterFieldMap,
} from "./helpers";
import { convertSavedViewToDataTableConfig, getUserName } from "@/react/shared/helper";
import { ProjectTearsheetProvider } from "../tearsheets/project/project-tearsheet";
import moment from "moment-timezone";
import { DateUtils } from "@/lib/utils/date";
import type { CustomFieldProjectType, SortModel } from "@/react/prop-types";
import { useGetCustomFields, useGetSavedView } from "../common/queries/queries";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import type { ProjectListData } from "./project-list-prop-types";
import type { ProjectStatus as ProjectStatusType } from "@laborchart-modules/common/dist/rethink/schemas/enums/projects";
import { ProjectStatus } from "@laborchart-modules/common/dist/rethink/schemas/enums/projects";
import {
   timeOptions,
   timeZoneOptions,
   TIME_ZONES_MAP,
   TIME_ZONES_MAP_REVERSED,
} from "@/lib/utils/timezones";
import type { TimeZonesMapValues } from "@/lib/utils/timezones";
import { FieldMapping } from "./project-list-enums";
import { CustomFieldType, type SavedView } from "@laborchart-modules/common";
import { FileDownloadToastProvider } from "../data-table/DataExport/FileDownload";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { useGetProjectRoleOptions } from "./queries";
import { useViewPreferenceContext } from "@/react/providers/permission-context-provider";
import { PersonTearsheetProvider } from "../tearsheets/people/people-tearsheet";
import { createRoleAttrs, type ListServerFilter } from "../data-table/data-table-utils";
import type { FindProjectsPaginatedQueryParams } from "@laborchart-modules/lc-core-api/dist/api/projects/find-projects";
import { useGetProcoreEnvironment } from "../common/queries/procore-queries";

export function ProjectListContainer({ setTitle }: { setTitle: (title: string) => void }) {
   const { getViewPreference } = useViewPreferenceContext();
   const isLastNameFirst = getViewPreference()?.displayLastNamesFirst() ?? false;
   const { groupId } = useGroupContext();
   const I18n = useI18nContext();
   const { data: groupOptions } = useGetGroupOptionsQuery();
   const [tableApi, setTableApi] = React.useState<TableApi>();
   const { data: customFields } = useGetCustomFields(CustomFieldEntity.PROJECT);
   const { data: roleOptions } = useGetProjectRoleOptions(groupId);
   const [convertedSavedView, setConvertedSavedView] = React.useState<any>();
   const { data: procoreEnvironment } = useGetProcoreEnvironment();
   const url = new URL(window.location.href);
   const params = url.searchParams;
   const [viewId] = React.useState(params.get("viewId"));

   const { data: savedViewData } = useGetSavedView(viewId);

   // I don't like this implementation but it fixes a production issue now.
   // On future pages we need to figure out how to prevent constant re-rendering.
   React.useEffect(() => {
      if (viewId && savedViewData && customFields && !convertedSavedView) {
         const convertedSavedViewData = convertSavedViewToDataTableConfig(
            savedViewData?.data as SavedView,
            columnHeadersMap,
            defaultProjectListTableConfig,
            filterFieldMap,
            filterRendererMap,
            customFields,
            roleOptions,
         );
         setConvertedSavedView(convertedSavedViewData);
         setTitle(savedViewData.data?.name ?? I18n.t("views.company.workforce_planning.list"));
      }
   }, [
      groupId,
      viewId,
      savedViewData,
      convertedSavedView,
      setConvertedSavedView,
      customFields,
      roleOptions,
   ]);

   const handleTableReady = (api: TableApi) => setTableApi(api);

   const fetchProjectList = async (
      filters: ListServerFilter[],
      sortModel: SortModel[],
      startingAfter?: string,
      search?: string,
   ) => {
      const coreApiFilters = convertProjectDataTableFilters(filters);

      const coreApiSort = convertDataTableSort(sortModel);
      const params = {
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         limit: 100,
         starting_after: startingAfter ?? undefined,
         search: search,
         sort_by: coreApiSort.sort_by,
         sort_direction: coreApiSort.direction,
         custom_field_id: coreApiSort.custom_field_id, // required when sorted by custom field
         group_id: groupId == "my-groups" ? undefined : groupId,
         filters: coreApiFilters,
      } as FindProjectsPaginatedQueryParams;
      const projectData = await ProjectStore.getProjectList(params).payload;

      const projectListDetails = [];

      for (const projectList of projectData.data) {
         const wage_overrides = projectList.wage_overrides;
         const projectListOptions = filterGroupDetails({
            groupOptions,
            projectGroupList: projectList.group_ids,
         });

         const tags = projectList.tag_instances?.map((tag) => ({
            label: tag.tag.abbreviation,
            color: tag.tag.color,
            value: tag.tag.id,
            name: tag.tag.name,
            shape: "pill",
            componentName: "multiselect",
         }));
         let projectStatus = {
            value: projectList.status,
            label: "views.company.workforce_planning.inactive",
            color: "gray",
         };
         if (projectList.status === ProjectStatus.ACTIVE) {
            projectStatus = {
               value: projectList.status,
               label: "views.company.workforce_planning.active",
               color: "green",
            };
         }
         if (projectList.status === ProjectStatus.PENDING) {
            projectStatus = {
               value: projectList.status,
               label: "views.company.workforce_planning.pending",
               color: "yellow",
            };
         }
         //calling the utility function to generate role attributes
         /* istanbul ignore next */
         const roleAttrs: Record<string, any> = createRoleAttrs(projectList.roles);
         // Initialize the base project details
         const projectDetails: Record<string, any> = {
            ...projectList,
            cornerstone_stage: {
               id: (projectList as any).cornerstone_stage_id,
               name: (projectList as any).cornerstone_stage_name,
            },
            project_name: projectList.name,
            color: {
               label: "",
               color: projectList.color ? projectList.color : "",
               shape: "square",
            },
            start_date: projectList.start_date ? new Date(projectList.start_date) : null,
            est_end_date: projectList.est_end_date ? new Date(projectList.est_end_date) : null,
            project_hash: projectList.job_number,
            project_type: projectList.project_type,
            postal: projectList.zipcode,
            created_at: projectList.created_at ? new Date(projectList.created_at) : null,
            created_by: getUserName(projectList.creator_name, isLastNameFirst),
            est_avg_rate: projectList.bid_rate ?? "",
            percent_complete: projectList.percent_complete,
            tag_instances: tags,
            wage_overrides: wage_overrides
               ? wage_overrides.map((wo) => ({
                    label: I18n.t(
                       "views.company.workforce_planning.projects.wage_overrides_value",
                       {
                          wage_overrides_position_name: wo.position_name,
                          wage_overrides_rate: wo.rate,
                          currency_symbol: procoreEnvironment?.currency_symbol ?? "$",
                       },
                    ),
                 }))
               : "",
            groups: projectListOptions,
            status: projectStatus,
            timezone: timeZoneOptions.find(
               ({ id }) =>
                  id ===
                  TIME_ZONES_MAP_REVERSED[(projectList.timezone ?? "") as TimeZonesMapValues],
            )!,
            daily_start_time: {
               id: projectList.daily_start_time,
               label: timeOptions.find(({ id }) => id === projectList.daily_start_time)!.label,
            },
            daily_end_time: {
               id: projectList.daily_end_time,
               label: timeOptions.find(({ id }) => id === projectList.daily_end_time)!.label,
            },
            procore_id: projectList.procore_id,
            ...roleAttrs,
         };

         // Adding custom fields as individual attributes
         if (projectList.custom_fields) {
            projectList.custom_fields.forEach((customField: CustomFieldProjectType) => {
               const { value, type, integration_name } = customField;
               if (type === CustomFieldType.DATE && value) {
                  projectDetails[integration_name] = DateUtils.getAttachedDate(value);
               } else if (type === CustomFieldType.HEX_COLOR && value) {
                  projectDetails[integration_name] = {
                     label: "",
                     color: value,
                     shape: "square",
                  };
               } else {
                  projectDetails[integration_name] = value;
               }
            });
         }
         projectListDetails.push(projectDetails);
      }
      return { data: projectListDetails, pagination: projectData.pagination };
   };

   //Edit project - inline update project details
   /* istanbul ignore next */
   const updateProject = async (params: CellValueChangeParams<any>) => {
      const { id, timezone } = params.rowData as ProjectListData;

      // Extract 'fieldName' and 'newValue' from 'params'
      const { field: fieldName, newValue } = params;

      const maybeCustomField = customFields?.find((x) => x.integration_name === fieldName);

      // Initialize 'payload' with 'id' and the updated field. For custom fields, the
      // payload key should be the custom field ID.
      const payload = { id, [fieldName]: newValue };

      // Check and update fields based on 'fieldName' due to the different data types and properties
      if (fieldName === FieldMapping.GROUPS) {
         payload[FieldMapping.GROUPS] = filterGroupIds({ groupOptions, editedGroupIds: newValue });
      } else if (fieldName === FieldMapping.START_DATE) {
         payload[fieldName] = newValue ? newValue.getTime() : null;
      } else if (fieldName === FieldMapping.EST_END_DATE) {
         payload[fieldName] = newValue ? newValue.getTime() : null;
      } else if (
         fieldName === FieldMapping.DAILY_START_TIME ||
         fieldName === FieldMapping.DAILY_END_TIME
      ) {
         payload[fieldName] = newValue.id;
      } else if (fieldName === FieldMapping.STATUS) {
         payload[fieldName] = newValue.value as ProjectStatusType;
      } else if (fieldName === FieldMapping.TIMEZONE) {
         payload[fieldName] = TIME_ZONES_MAP[timezone.id];
      } else if (
         fieldName === FieldMapping.BID_RATE ||
         fieldName === FieldMapping.PERCENT_COMPLETE
      ) {
         payload[fieldName] = Number(newValue);
      } else if (fieldName === FieldMapping.COLOR) {
         payload[fieldName] = newValue.color;
      } else if (maybeCustomField) {
         delete payload[fieldName];
         let value = newValue;
         if (maybeCustomField.type === CustomFieldType.HEX_COLOR) {
            value = newValue.color;
         }
         if (maybeCustomField.type === CustomFieldType.DATE) {
            value = getDetachedDay(newValue);
         }
         if (
            maybeCustomField.type === CustomFieldType.NUMBER ||
            maybeCustomField.type === CustomFieldType.CURRENCY
         ) {
            value = Number(newValue);
         }
         payload["custom_fields"] = { [maybeCustomField.id]: value };
      }

      // Sending the updated payload to ProjectStore and returning the response
      const response = ProjectStore.updateProjectsStream([payload]);
      return response;
   };

   const currentDateTime = moment().format("YYYYMMDD_HHmmss");

   return (
      /*wrapping the component in PersonTearsheetProvider along with the existing ProjectTearsheetProvider
      because we will be opening the Person Details Tearsheet when someone click on Person's Name from the Role columns.
      ProjectTearsheetProvider must be outermost so PersonTearsheet can use useProjectTearsheet()
      **/
      <ProjectTearsheetProvider projectsTableApi={tableApi}>
         <PersonTearsheetProvider peopleTableApi={tableApi}>
            <FileDownloadToastProvider>
               <ToolLandingPage
                  className={"border-box project-list-detail-page data-table-list-detail-page"}
               >
                  <ToolLandingPage.Main className={"project-list-page-main"}>
                     <ToolLandingPage.Body className={"project-list-page-body"}>
                        <SplitViewCard className={"project-list-card"}>
                           <SplitViewCard.Section className="project-list-section data-table-list-section">
                              <Box
                                 style={{
                                    marginTop: "5px",
                                    height: "100%",
                                 }}
                              >
                                 {!viewId && groupOptions && roleOptions && customFields && (
                                    <ProjectListDataTable
                                       fetchProjectList={fetchProjectList}
                                       groupId={groupId}
                                       savedView={null}
                                       currentDateTime={currentDateTime}
                                       tableApi={tableApi}
                                       handleTableReady={handleTableReady}
                                       customFields={customFields}
                                       groupOptions={groupOptions}
                                       updateProject={updateProject}
                                       roleOptions={roleOptions}
                                       isLastNameFirst={isLastNameFirst}
                                       filterRendererMap={filterRendererMap}
                                    />
                                 )}

                                 {/* If the view ID is present, wait until the saved view is loaded from the API to render the data table */}
                                 {viewId &&
                                    convertedSavedView &&
                                    groupOptions &&
                                    customFields &&
                                    roleOptions && (
                                       <ProjectListDataTable
                                          fetchProjectList={fetchProjectList}
                                          groupId={groupId}
                                          currentDateTime={currentDateTime}
                                          tableApi={tableApi}
                                          handleTableReady={handleTableReady}
                                          customFields={customFields}
                                          groupOptions={groupOptions}
                                          updateProject={updateProject}
                                          savedView={convertedSavedView}
                                          roleOptions={roleOptions}
                                          isLastNameFirst={isLastNameFirst}
                                          filterRendererMap={filterRendererMap}
                                       />
                                    )}
                              </Box>
                           </SplitViewCard.Section>
                        </SplitViewCard>
                     </ToolLandingPage.Body>
                  </ToolLandingPage.Main>
               </ToolLandingPage>
            </FileDownloadToastProvider>
         </PersonTearsheetProvider>
      </ProjectTearsheetProvider>
   );
}
